import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';

import './styles/fonts.js';
import './styles/tailwind.css';
import './styles/shell-tokens.css';
import './styles/shell.css';

import { AppRouter } from './app/AppRouter.js';
import { SiteContextGate } from './app/SiteContextGate.js';
import { ConfirmHost } from './components/ui/index.js';
import { routerBasename } from './lib/basePath.js';
import { applyFont, ensureFontLoaded, loadFontTheme } from './lib/fontTheme.js';
import { i18n, initI18n } from './lib/i18n.js';
import { applyPalette, loadPalette } from './lib/palette.js';

// Apply the persisted light/dark + color palette + font before first paint so
// there is no flash of the wrong theme. useTheme / PaletteSwitcher keep these
// in sync afterwards. The font stack is applied immediately and its files are
// fetched in parallel — @fontsource ships font-display:swap, so text paints in
// the fallback and re-flows once, rather than blocking the first render.
(function bootTheme(): void {
  const el = document.documentElement;
  let theme = el.getAttribute('data-theme') ?? 'dark';
  try {
    const saved = window.localStorage.getItem('erp-theme');
    if (saved === 'dark' || saved === 'light') theme = saved;
  } catch {
    /* ignore */
  }
  el.setAttribute('data-theme', theme);
  el.classList.toggle('dark', theme === 'dark');
  applyPalette(loadPalette(), theme === 'dark');
  const font = loadFontTheme();
  applyFont(font);
  ensureFontLoaded(font);
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

await initI18n();

// Re-apply the font stack whenever the language changes. Not cosmetic: Kurdish
// needs a face that has ڕ ڵ ۆ ێ, which the default theme's Arabic family lacks
// (see fontTheme.isKurdish), and switching INTO Kurdish is exactly the moment
// that has to be noticed. The detected boot language is handled by the same call
// in bootTheme reading <html lang>.
applyFont(loadFontTheme(), i18n.language);
i18n.on('languageChanged', (lng: string) => applyFont(loadFontTheme(), lng));

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

createRoot(root).render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={routerBasename()}>
          <SiteContextGate>
            <AppRouter />
          </SiteContextGate>
          <ConfirmHost />
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>,
);
